// src/components/resources/ResourceCard.js
// Premium resource card with sophisticated hover interactions

import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Clock, ArrowUpRight, Building2, TrendingUp, Star } from 'lucide-react';
import './styles/resource-card.css';

// Type configuration
const TYPE_CONFIG = {
  article: {
    badge: 'Article',
    badgeClass: 'resource-card__badge--article',
    linkPrefix: '/articles/',
    icon: null,
  },
  caseStudy: {
    badge: 'Case Study',
    badgeClass: 'resource-card__badge--case-study',
    linkPrefix: '/case-studies/',
    icon: Building2,
  },
  news: {
    badge: 'News',
    badgeClass: 'resource-card__badge--news',
    linkPrefix: '/articles/',
    icon: TrendingUp,
  },
};

// Card animation variants
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const ResourceCard = ({ resource, index = 0, featured = false }) => {
  const prefersReducedMotion = useReducedMotion();
  const config = TYPE_CONFIG[resource.type] || TYPE_CONFIG.article;
  const linkTo = `${config.linkPrefix}${resource.slug}`;

  // Format date
  const formattedDate = resource.publishedFormatted ||
    new Date(resource.publishedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  // Determine meta content based on type
  const getMeta = () => {
    if (resource.type === 'caseStudy') {
      return (
        <>
          {resource.clientName && (
            <span className="resource-card__meta-client">{resource.clientName}</span>
          )}
          {resource.industry && (
            <>
              <span className="resource-card__meta-dot">·</span>
              <span>{resource.industry}</span>
            </>
          )}
        </>
      );
    }
    return (
      <>
        {resource.author?.name && (
          <span className="resource-card__meta-author">
            {resource.author.avatarUrl && (
              <img
                src={`${resource.author.avatarUrl}?w=48&h=48&fit=fill&q=80`}
                alt={resource.author.name}
                className="resource-card__author-avatar"
              />
            )}
            {resource.author.name}
          </span>
        )}
        {resource.category && (
          <>
            <span className="resource-card__meta-dot">·</span>
            <span>{resource.category}</span>
          </>
        )}
        {resource.readingTime && (
          <>
            <span className="resource-card__meta-dot">·</span>
            <span className="resource-card__meta-time">
              <Clock size={12} />
              {resource.readingTime}
            </span>
          </>
        )}
      </>
    );
  };

  return (
    <motion.article
      className={`resource-card ${featured ? 'resource-card--featured' : ''}`}
      variants={prefersReducedMotion ? {} : cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      style={{
        transitionDelay: prefersReducedMotion ? '0ms' : `${index * 60}ms`,
      }}
    >
      <Link to={linkTo} className="resource-card__link">
        {/* Image Container */}
        <div className="resource-card__image-wrap">
          {resource.heroImage?.url ? (
            <img
              src={`${resource.heroImage.url}?w=600&h=340&fit=fill&q=80&fm=webp`}
              alt={resource.heroImage.alt || resource.title}
              className="resource-card__image"
              loading="lazy"
            />
          ) : (
            <div className="resource-card__image-placeholder">
              {config.icon && <config.icon size={48} strokeWidth={1} />}
            </div>
          )}

          {/* Badge */}
          <span className={`resource-card__badge ${config.badgeClass}`}>
            {config.badge}
          </span>

          {/* Featured indicator with star icon and glow */}
          {resource.isFeatured && (
            <span className="resource-card__featured-badge">
              <Star size={12} />
              Featured
            </span>
          )}

          {/* Hover Overlay */}
          <div className="resource-card__overlay">
            <span className="resource-card__read-cta">
              <span>Read More</span>
              <ArrowUpRight size={16} />
            </span>
          </div>

          {/* Gradient overlay for text readability */}
          <div className="resource-card__image-gradient" />
        </div>

        {/* Content */}
        <div className="resource-card__content">
          {/* Date line */}
          <div className="resource-card__date">{formattedDate}</div>

          {/* Title */}
          <h3 className="resource-card__title">{resource.title}</h3>

          {/* Meta line */}
          <div className="resource-card__meta">{getMeta()}</div>

          {/* Summary */}
          {resource.summary && (
            <p className="resource-card__summary">{resource.summary}</p>
          )}

          {/* Headline outcome for case studies */}
          {resource.headlineOutcome && (
            <div className="resource-card__outcome">
              <TrendingUp size={14} />
              <span>{resource.headlineOutcome}</span>
            </div>
          )}

          {/* Topics */}
          {resource.topics?.length > 0 && (
            <div className="resource-card__tags">
              {resource.topics.slice(0, 3).map((topic) => (
                <span key={topic.id || topic.name} className="resource-card__tag">
                  {topic.name}
                </span>
              ))}
              {resource.topics.length > 3 && (
                <span className="resource-card__tag resource-card__tag--more">
                  +{resource.topics.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Decorative border glow on hover */}
        <div className="resource-card__glow" />
      </Link>
    </motion.article>
  );
};

export default ResourceCard;
